/**
 * iter482 — Phase 4. Budget change history panel.
 * Rendered on: (1) Site edit dialog inside Customer master and
 * (2) Revenue Budget site-override dialog.
 * Shows every reverse-write from Revenue Budget → sites master with a
 * per-row Revert button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import { RotateCcw } from 'lucide-react';

const FIELD_LABELS = {
  forecast_price: '£ / job',
  audit_frequency: 'Frequency',
  custom_frequency_days: 'Custom days',
  primary_service_id: 'Service',
};

export default function BudgetHistoryPanel({ siteId }) {
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [master, setMaster] = useState(null); // iter495j
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [logRes, mstrRes] = await Promise.all([
        api.get(`/reports/revenue-budget/change-log?site_id=${siteId}&limit=100`),
        api.get(`/reports/revenue-budget/site/${siteId}/master-price`),
      ]);
      setEntries(logRes.data || []);
      setMaster(mstrRes.data || null);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const revert = async (id) => {
    if (!window.confirm('Revert this change? Master value will be restored to OLD.')) return;
    try {
      await api.post(`/reports/revenue-budget/change-log/${id}/revert`);
      toast({ title: 'Reverted', description: 'Master restored to previous value.' });
      await load();
    } catch (err) {
      toast({
        title: 'Revert failed',
        description: err?.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-2" data-testid="budget-history-panel">
      {/* iter495j — Effective master price panel. Shows the value
          that flows to every future job (via sites.forecast_price
          fallback) and any scheduled changes queued from Review &
          Publish. Compounding-free — mirrors the Revenue Budget. */}
      {master && (
        <div className="border rounded-lg p-3 bg-emerald-50/50" data-testid="site-master-price-panel">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-emerald-800/70 font-semibold">Effective master price</div>
              <div className="text-lg font-bold text-emerald-900">
                {master.current_master_price != null
                  ? `£${Number(master.current_master_price).toFixed(2)}`
                  : '— (no master set)'}
                {master.audit_frequency && (
                  <span className="ml-2 text-xs font-normal text-slate-600">
                    · {master.audit_frequency}{master.custom_frequency_days ? ` (${master.custom_frequency_days}d)` : ''}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-500">
                This is the live price used for all new jobs.
              </div>
            </div>
          </div>
          {master.schedule && master.schedule.length > 0 && (
            <div className="mt-2 border-t border-emerald-200 pt-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-600 font-semibold mb-1">Scheduled price changes</div>
              <div className="max-h-32 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left py-0.5">Effective</th>
                      <th className="text-right py-0.5">Price</th>
                      <th className="text-left py-0.5 pl-2">Frequency</th>
                      <th className="text-left py-0.5 pl-2">Source</th>
                      <th className="text-left py-0.5 pl-2">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {master.schedule.map(row => (
                      <tr key={`s-${row.id}`} className={`border-t border-emerald-100 ${row.is_superseded ? 'opacity-50' : row.is_future ? '' : 'text-slate-500'}`}>
                        <td className="py-0.5">
                          {row.effective_from}
                          {row.is_superseded && (
                            <span className="ml-1 text-[9px] rounded bg-slate-200 text-slate-500 px-1 line-through">superseded</span>
                          )}
                          {!row.is_superseded && row.is_future && (
                            <span className="ml-1 text-[9px] rounded bg-amber-100 text-amber-800 px-1">future</span>
                          )}
                        </td>
                        <td className="py-0.5 text-right font-mono">{row.price != null ? `£${Number(row.price).toFixed(2)}` : '—'}</td>
                        <td className="py-0.5 pl-2">{row.frequency || '—'}</td>
                        <td className="py-0.5 pl-2 text-[10px]">{(row.source || '').replace(/_/g, ' ')}</td>
                        <td className="py-0.5 pl-2 text-[10px]">{row.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-sm font-semibold flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="toggle-budget-history"
      >
        <RotateCcw className="h-4 w-4" /> Budget change history
        <span className="text-[10px] font-normal">
          ({entries.length}{loading ? '…' : ''})
        </span>
        <span className="text-xs">{expanded ? '▼' : '▶'}</span>
      </button>
      {expanded && (
        <div className="border rounded-lg overflow-hidden">
          {entries.length === 0 ? (
            <div className="text-xs text-muted-foreground italic p-3 text-center">
              No reverse-writes yet for this site. Changes promoted from Revenue Budget will appear here with a Revert action.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Field</th>
                  <th className="text-left px-2 py-1.5">Old</th>
                  <th className="text-left px-2 py-1.5">New</th>
                  <th className="text-left px-2 py-1.5">By</th>
                  <th className="text-left px-2 py-1.5">When</th>
                  <th className="text-left px-2 py-1.5">Source</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    className={`border-t ${e.reverted_at ? 'opacity-50 line-through' : ''}`}
                    data-testid={`site-history-row-${e.id}`}
                  >
                    <td className="px-2 py-1.5 font-medium">{FIELD_LABELS[e.field] || e.field}</td>
                    <td className="px-2 py-1.5 text-rose-600">{e.old_value ?? '—'}</td>
                    <td className="px-2 py-1.5 text-emerald-700">{e.new_value ?? '—'}</td>
                    <td className="px-2 py-1.5 text-slate-600">{e.changed_by_name}</td>
                    <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                      {e.changed_at ? new Date(e.changed_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-[10px] text-slate-500 uppercase">
                      {e.source?.replace('budget_', '') || '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      {!e.reverted_at && e.source !== 'budget_revert' && (
                        <button
                          type="button"
                          onClick={() => revert(e.id)}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
                          data-testid={`site-revert-${e.id}`}
                        >
                          Revert
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
